import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import RichTextEditor from "@/components/common/RichTextEditor";
import AIAssistBar from "@/components/ai/AIAssistBar";
import MediaAnalyseChip from "@/components/media/MediaAnalyseChip";
import ImageCropDialog from "@/components/home/ImageCropDialog";
import { AnonymizeDialog } from "@/components/media/AnonymizeDialog";
import { AnonymityNotice } from "@/components/media/AnonymityNotice";
import EmojiPicker from "@/components/EmojiPicker";
import Brand from "@/components/common/Brand";
import { StreetAutocomplete } from "@/components/address/StreetAutocomplete";
import { useCityAutofill } from "@/lib/address";
import StxProfileEditor from "@/components/profile/StxProfileEditor";
import { ArrowLeft, Camera, Loader2, User } from "lucide-react";

export default function EditProfilePage() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [cooldownDays, setCooldownDays] = useState(0);
  const [handle, setHandle] = useState("");
  const [bio, setBio] = useState("");
  const [capabilities, setCapabilities] = useState([]);
  // Persönliche Daten
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [street, setStreet] = useState("");
  const [houseNr, setHouseNr] = useState("");
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("DE");
  useCityAutofill(country, zip, city, setCity);
  // iter80.7: Avatar-Upload
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  // iter150 (2026-02-24) · zweites, breites Profilbild (Cover)
  const [bannerUrl, setBannerUrl] = useState("");
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const bannerFileRef = useRef(null);
  const bannerCameraRef = useRef(null);
  // iter278 · Creator-Card-Ausschnitt (3:1) aus dem Profilbild
  const [creatorCardUrl, setCreatorCardUrl] = useState("");
  const [showCardCrop, setShowCardCrop] = useState(false);
  const [cardBusy, setCardBusy] = useState(false);
  // iter152.5 · Crop-Dialog-State (iter152.6: für Avatar UND Cover)
  const [cropSource, setCropSource] = useState(null);
  const [cropTarget, setCropTarget] = useState(null); // 'avatar' | 'banner'
  // iter173e · Anonymisierungs-Engine zwischen Crop und Upload
  const [anonJob, setAnonJob] = useState(null); // { file, target }

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  // Sub-Batch E-1: Email-Change-Modal
  const [showEmailModal, setShowEmailModal] = useState(false);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    (async () => {
      try {
        const { data } = await api.get("/profile");
        setProfile(data);
        setDisplayName(data.display_name || "");
        setCooldownDays(data.display_name_cooldown_days_remaining || 0);
        setHandle(data.handle || "");
        setBio(data.bio || "");
        setCapabilities(data.capabilities || []);
        setFirstName(data.first_name || "");
        setLastName(data.last_name || "");
        setStreet(data.address_street || "");
        setHouseNr(data.address_house_number || "");
        setZip(data.address_zip || "");
        setCity(data.address_city || "");
        setCountry(data.address_country || "DE");
        setAvatarUrl(data.avatar_url || "");
        setBannerUrl(data.banner_url || "");
        setCreatorCardUrl(data.creator_card_url || "");
      } catch {
        navigate("/onboarding/profile");
      }
    })();
  }, [user, navigate]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(false); setSaving(true);
    try {
      await api.patch("/profile", {
        display_name: displayName, bio,
        avatar_url: avatarUrl,
        banner_url: bannerUrl,
        first_name: firstName, last_name: lastName,
        address_street: street, address_house_number: houseNr,
        address_zip: zip, address_city: city, address_country: country,
      });
      await refreshUser();
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "BIO_CONTAINS_CONTACT_DATA") {
        setError("Deine Bio enthält Kontaktdaten (E-Mail, Telefonnummer, Adresse oder Social-Handle) und kann so nicht gespeichert werden. Bitte entferne diese Informationen.");
      } else if (d?.code === "DISPLAY_NAME_COOLDOWN") {
        setError(d.message);
      } else if (d?.code === "DISPLAY_NAME_TAKEN") {
        setError(d.message);
      } else {
        setError(d?.message || d || formatError(err));
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    finally { setSaving(false); }
  };

  if (!profile) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;

  const fieldCls = "mt-1 w-full h-10 px-4 border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]";
  const lbl = "text-xs font-medium text-[#666] uppercase tracking-wider";

  return (
    <div data-testid="edit-profile-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A]">{t("edit_profile_extra.title")}</h1>
      </div>

      {error && <InfoCard variant="danger" size="sm">{error}</InfoCard>}
      {success && <InfoCard variant="success" size="sm">Profil gespeichert</InfoCard>}

      {/* Account-Section: Email + Geschlecht (lesbar, mit Aktion „Email ändern") */}
      <div className="px-4 pt-4">
        <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider mb-3">Konto</p>
        <div className="space-y-3 mb-5">
          <div className="flex items-start justify-between gap-3 p-3 border border-[#E5E5E5]">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-[#999] uppercase">{t("edit_profile_extra.email_addr")}</p>
              <p className="text-sm font-medium text-[#0A0A0A] truncate">{user?.email}</p>
              {user?.email_verified ? (
                <p className="text-[10px] text-green-600 mt-0.5">{t("edit_profile_extra.confirmed")}</p>
              ) : (
                <p className="text-[10px] text-amber-600 mt-0.5">{t("edit_profile_extra.not_confirmed")}</p>
              )}
            </div>
            <button type="button" data-testid="email-change-btn"
              onClick={() => setShowEmailModal(true)}
              className="rounded-full h-9 px-3 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium hover:bg-[#0A0A0A] hover:text-white shrink-0">
              Ändern
            </button>
          </div>
          {(user?.sex === "MALE" || user?.sex === "FEMALE") && (
            <div className="p-3 border border-[#E5E5E5] bg-[#FAFAFA]">
              <p className="text-[10px] text-[#999] uppercase">Geschlecht</p>
              <p className="text-sm font-medium text-[#0A0A0A]">
                {user.sex === "MALE" ? "♂ Männlich" : "♀ Weiblich"}
                <span className="ml-2 text-[10px] text-[#999] font-normal">{t("edit_profile_extra.not_editable")}</span>
              </p>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSave} className="px-4 pt-0 pb-4 space-y-5">
        {/* Social-Profil (eigener umrahmter Bereich) */}
        <div className="rounded-xl border border-[#E5E5E5] bg-white p-4">
          <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider mb-3">{t("edit_profile_extra.public_profile")}</p>
          <div className="mb-4"><AnonymityNotice location="profile_image" /></div>
          {/* iter80.7: Avatar-Upload */}
          <div className="flex items-center gap-4 mb-4">
            <div className="w-20 h-20 rounded-full bg-[#E5E5E5] overflow-hidden flex items-center justify-center shrink-0">
              {avatarUrl ? (
                <img data-testid="edit-avatar-preview" src={avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <User size={32} className="text-[#999]" />
              )}
            </div>
            <div className="flex-1 space-y-2">
              <p className="text-xs text-[#666]">Avatar (empfohlen: quadratisch, min 200×200)</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="edit-avatar-gallery-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={avatarUploading}
                  className="flex-1 h-9 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium flex items-center justify-center gap-1 hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40">
                  {avatarUploading ? <Loader2 size={12} className="animate-spin" /> : <><Camera size={12} /> {t("stx_profile.upload", { defaultValue: "Bild wählen" })}</>}
                </button>
                {avatarUrl && (
                  <button
                    type="button"
                    data-testid="edit-avatar-remove-btn"
                    onClick={() => {
                      setAvatarUrl("");
                      setAvatarFile(null);
                      if (fileRef.current) fileRef.current.value = "";
                      if (cameraRef.current) cameraRef.current.value = "";
                    }}
                    disabled={avatarUploading}
                    className="rounded-full h-9 px-3 border border-[#E5E5E5] text-red-500 text-xs disabled:opacity-40">
                    ✕
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  setCropSource(f); setCropTarget("avatar");
                  e.target.value = "";
                }} />
              <input ref={cameraRef} type="file" accept="image/*" capture="user" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  setCropSource(f); setCropTarget("avatar");
                  e.target.value = "";
                }} />
            </div>
          </div>
          {avatarFile && !avatarUploading && (
            <MediaAnalyseChip
              file={avatarFile}
              testIdSuffix="edit-avatar"
            />
          )}

          {/* iter324 · Social-Cover wieder aktiviert (Profil-Vervollständigung verlangt es). */}
          <>
          <div data-testid="edit-cover-block" className="flex items-start gap-4 mt-5 mb-4">
            <div className="w-24 rounded-lg bg-[#E5E5E5] overflow-hidden flex items-center justify-center shrink-0" style={{ aspectRatio: "16 / 9" }}>
              {bannerUrl ? (
                <img data-testid="edit-banner-preview" src={bannerUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <p className="text-[10px] text-[#999]">16 : 9</p>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <p className="text-xs text-[#666]">
                {t("edit_profile_extra.cover_label")}
              </p>
              <p className="text-[10px] text-[#999]">
                {t("edit_profile_extra.cover_desc")}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="edit-banner-gallery-btn"
                  onClick={() => bannerFileRef.current?.click()}
                  disabled={bannerUploading}
                  className="flex-1 h-9 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium flex items-center justify-center gap-1 hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40"
                >
                  {bannerUploading ? <Loader2 size={12} className="animate-spin" /> : <>{t("edit_profile_extra.from_gallery")}</>}
                </button>
                <button
                  type="button"
                  data-testid="edit-banner-camera-btn"
                  onClick={() => bannerCameraRef.current?.click()}
                  disabled={bannerUploading}
                  className="flex-1 h-9 border border-[#E5E5E5] text-[#666] text-xs font-medium flex items-center justify-center gap-1 hover:bg-[#F5F5F5] disabled:opacity-40"
                >
                  <Camera size={12} /> {t("edit_profile_extra.camera")}
                </button>
                {bannerUrl && (
                  <button
                    type="button"
                    data-testid="edit-banner-remove-btn"
                    onClick={() => {
                      setBannerUrl("");
                      setBannerFile(null);
                      if (bannerFileRef.current) bannerFileRef.current.value = "";
                      if (bannerCameraRef.current) bannerCameraRef.current.value = "";
                    }}
                    disabled={bannerUploading}
                    className="rounded-full h-9 px-3 border border-[#E5E5E5] text-red-500 text-xs disabled:opacity-40"
                  >
                    ✕
                  </button>
                )}
              </div>
              <input
                ref={bannerFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]; if (!f) { return; }
                  setCropSource(f);
                  setCropTarget("banner");
                  e.target.value = "";
                }}
              />
              <input
                ref={bannerCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]; if (!f) { return; }
                  setCropSource(f);
                  setCropTarget("banner");
                  e.target.value = "";
                }}
              />
            </div>
          </div>
          {bannerFile && !bannerUploading && (
            <MediaAnalyseChip file={bannerFile} testIdSuffix="edit-banner" />
          )}

          </>

          {/* iter278 · Creator Card (STX-Konzept) im Social-Editor ausgeblendet (iter324). */}
          {false && (<>
          {/* iter278 · Creator Card anpassen · 3:1-Ausschnitt aus dem Profilbild */}
          <div data-testid="creator-card-block" className="mt-5 mb-4 p-3 border border-[#E5E5E5] rounded-lg">
            <p className="text-xs font-medium text-[#0A0A0A]">{t("edit_profile_extra.creator_card_title")}</p>
            <p className="text-[10px] text-[#999] mt-0.5">
              {t("edit_profile_extra.creator_card_desc")}
            </p>
            {bannerUrl ? (
              <div className="flex items-start gap-4 mt-3">
                <div className="w-24 rounded-lg overflow-hidden bg-[#E5E5E5] shrink-0" style={{ aspectRatio: "3 / 1" }}>
                  <img
                    data-testid="creator-card-preview"
                    src={creatorCardUrl || bannerUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <button
                    type="button"
                    data-testid="creator-card-crop-btn"
                    onClick={() => setShowCardCrop(true)}
                    disabled={cardBusy}
                    className="h-9 px-4 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium flex items-center justify-center gap-1 hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40"
                  >
                    {cardBusy ? <Loader2 size={12} className="animate-spin" /> : <>{t("edit_profile_extra.creator_card_crop_btn")}</>}
                  </button>
                  {creatorCardUrl && (
                    <p className="text-[10px] text-green-600">{t("edit_profile_extra.creator_card_set")}</p>
                  )}
                </div>
              </div>
            ) : (
              <div data-testid="creator-card-disabled-hint" className="mt-3 rounded-lg border border-dashed border-[#E5E5E5] bg-[#FAFAFA] p-2 text-[11px] text-[#999]">
                {t("edit_profile_extra.creator_card_disabled")}
              </div>
            )}
          </div>

          {/* Hinweis: Beide Bilder erforderlich für öffentliches Profil */}
          {(!avatarUrl || !bannerUrl) && (
            <div data-testid="edit-profile-visibility-hint" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              <b>Hinweis:</b> {t("edit_profile_extra.public_visibility_prefix")} <b>Avatar</b> als auch <b>Profilbild (Cover)</b> hochgeladen sein.
            </div>
          )}
          </>)}
          <div className="space-y-3">
            <div>
              <label className={lbl}>Username</label>
              <div className="relative">
                <input
                  data-testid="edit-display-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value.slice(0, 20))}
                  className={fieldCls}
                  required
                  minLength={3}
                  maxLength={20}
                />
                {/* iter165 · Emoji-Picker rechts oben, öffnet sich als Panel unten */}
                <div className="absolute right-1 top-1/2 -translate-y-1/2">
                  <EmojiPicker onPick={(e) => setDisplayName((v) => (v + e).slice(0, 20))} />
                </div>
              </div>
              <div className="flex items-center justify-between mt-0.5">
                <p className="text-[10px] text-[#CCC]">{t("edit_profile_extra.public_name_hint")}</p>
                <p data-testid="edit-display-name-counter" className="text-[10px] text-[#999]">{displayName.length}/20</p>
              </div>
              {cooldownDays > 0 && (
                <p data-testid="username-cooldown-hint" className="text-[10px] text-[#666] mt-0.5">
                  Du kannst deinen Anzeigenamen nur alle 6 Monate ändern. Nächste Änderung möglich in {cooldownDays} Tag(en).
                </p>
              )}
            </div>
            <div>
              <label className={lbl}>Bio</label>
              <div data-testid="edit-bio">
                <RichTextEditor
                  value={bio}
                  onChange={(html) => setBio(html)}
                  placeholder={t("edit_profile_extra.bio_placeholder", { defaultValue: "Erzähle etwas über dich …" })}
                  minHeight="120px"
                  testId="edit-bio-editor"
                />
              </div>
              <p className="text-right text-[10px] text-[#CCC]">
                {(() => {
                  const plain = (bio || "").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ");
                  return `${plain.length}/500`;
                })()}
              </p>
              <AIAssistBar area="SOCIAL" value={bio} onApply={setBio} fieldType="bio" maxChars={500} testIdSuffix="edit-bio" />
            </div>
          </div>
        </div>

        {/* Handle bleibt system-intern — keine Public-Profil-URL-Anzeige mehr (Owner-Vorgabe 2026-07-05). */}

        {/* Persönliche Daten */}
        <div>
          <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider mb-3">{t("edit_profile_extra.personal_data")}</p>
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="flex-1">
                <label className={lbl}>Vorname</label>
                <input data-testid="edit-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={fieldCls} placeholder="Vorname" />
              </div>
              <div className="flex-1">
                <label className={lbl}>Nachname</label>
                <input data-testid="edit-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} className={fieldCls} placeholder="Nachname" />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className={lbl}>{t("edit_profile_extra.street")}</label>
                <StreetAutocomplete
                  testId="edit-street"
                  value={street}
                  onChange={setStreet}
                  country={country}
                  postalCode={zip}
                  locality={city}
                  className={fieldCls}
                  placeholder={t("edit_profile_extra.street")}
                />
              </div>
              <div className="w-24">
                <label className={lbl}>Hausnr.</label>
                <input data-testid="edit-house-nr" value={houseNr} onChange={(e) => setHouseNr(e.target.value)} className={fieldCls} placeholder="Nr." />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="w-28">
                <label className={lbl}>PLZ</label>
                <input data-testid="edit-zip" value={zip} onChange={(e) => setZip(e.target.value)} className={fieldCls} placeholder="PLZ" />
              </div>
              <div className="flex-1">
                <label className={lbl}>Stadt</label>
                <input data-testid="edit-city" value={city} onChange={(e) => setCity(e.target.value)} className={fieldCls} placeholder="Stadt" />
              </div>
            </div>
            <div>
              <label className={lbl}>Land</label>
              <select data-testid="edit-country" value={country} onChange={(e) => setCountry(e.target.value)} className={fieldCls}>
                <option value="DE">Deutschland</option>
                <option value="AT">{t("country.at")}</option>
                <option value="CH">Schweiz</option>
                <option value="NL">Niederlande</option>
                <option value="BE">Belgien</option>
                <option value="LU">Luxemburg</option>
                <option value="FR">Frankreich</option>
                <option value="IT">Italien</option>
                <option value="ES">Spanien</option>
                <option value="PL">Polen</option>
                <option value="DK">{t("country.dk")}</option>
                <option value="SE">Schweden</option>
                <option value="CZ">Tschechien</option>
                <option value="GB">{t("country.gb")}</option>
                <option value="US">USA</option>
              </select>
            </div>
          </div>
        </div>

        <button data-testid="save-profile-btn" type="submit" disabled={saving}
          className="w-full rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-50">
          {saving ? "Wird gespeichert..." : "Speichern"}
        </button>
      </form>

      {/* P6 §10.2 / §11 · stilltouchedX-Profil (eigene öffentliche Identität) +
          freiwillige Verknüpfung. Nur für freigeschaltete STX-Seller bearbeitbar. */}
      <StxProfileEditor />

      {showEmailModal && (
        <EmailChangeModal
          currentEmail={user?.email}
          onClose={() => setShowEmailModal(false)}
          onSuccess={async () => {
            setShowEmailModal(false);
            setSuccess(true);
            try { await refreshUser(); } catch { /* ignore */ }
          }}
        />
      )}

      {/* iter278 · Generischer Crop-Dialog für Avatar (1:1) und Cover (16:9) */}
      {cropSource && cropTarget && (
        <ImageCropDialog
          file={cropSource}
          aspect={cropTarget === "banner" ? 16 / 9 : 1}
          title={cropTarget === "banner" ? t("edit_profile_extra.cover_crop_title") : t("edit_profile_extra.avatar_crop_title")}
          onCancel={() => { setCropSource(null); setCropTarget(null); }}
          onCropped={async (cropped) => {
            const target = cropTarget;
            setCropSource(null); setCropTarget(null);
            const isBanner = target === "banner";
            if (isBanner) setBannerFile(cropped); else setAvatarFile(cropped);
            // iter173e · Statt Direkt-Upload zuerst die Anonymisierungs-Engine öffnen.
            const named = new File([cropped], isBanner ? "cover.jpg" : "avatar.jpg", { type: cropped.type || "image/jpeg" });
            setAnonJob({ file: named, target });
          }}
        />
      )}

      {/* iter173e · Anonymisierungs-Engine (Gesicht/Tattoo/Muttermal) vor Upload */}
      {anonJob && (
        <AnonymizeDialog
          file={anonJob.file}
          context={anonJob.target === "banner" ? "PROFILE_COVER" : "PROFILE_AVATAR"}
          onCancel={() => setAnonJob(null)}
          onComplete={async (finalUrl) => {
            const isBanner = anonJob.target === "banner";
            setAnonJob(null);
            const setUploadingFn = isBanner ? setBannerUploading : setAvatarUploading;
            const setUrlFn = isBanner ? setBannerUrl : setAvatarUrl;
            const failMsg = isBanner ? "Profilbild-Upload fehlgeschlagen" : "Avatar-Upload fehlgeschlagen";
            setUploadingFn(true);
            try {
              const newUrl = `${finalUrl}?v=${Date.now()}`;
              setUrlFn(newUrl);
              await api.patch("/profile", isBanner ? { banner_url: newUrl } : { avatar_url: newUrl });
              await refreshUser();
            } catch (persistErr) {
              setError(formatError(persistErr) || failMsg);
            } finally {
              setUploadingFn(false);
            }
          }}
        />
      )}

      {/* iter278 · Creator-Card-Crop (3:1) aus dem bereits hochgeladenen Profilbild.
          Serverseitiger Zuschnitt via Koordinaten → creator_card_url. */}
      {showCardCrop && bannerUrl && (
        <ImageCropDialog
          src={bannerUrl}
          aspect={3}
          returnCoords
          title={t("edit_profile_extra.creator_card_crop_title")}
          onCancel={() => setShowCardCrop(false)}
          onCropped={async (pixels) => {
            setShowCardCrop(false);
            setCardBusy(true);
            try {
              const { data } = await api.post("/profile/creator-card", {
                crop: {
                  x: Math.round(pixels.x),
                  y: Math.round(pixels.y),
                  width: Math.round(pixels.width),
                  height: Math.round(pixels.height),
                },
              });
              setCreatorCardUrl(`${data.creator_card_url}?v=${Date.now()}`);
              await refreshUser();
            } catch (cardErr) {
              const d = cardErr?.response?.data?.detail;
              setError(d?.message || d || formatError(cardErr) || t("edit_profile_extra.creator_card_error"));
              window.scrollTo({ top: 0, behavior: "smooth" });
            } finally {
              setCardBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}

// ─── Sub-Batch E-1: Email-Change-Modal ───────────────────
function EmailChangeModal({ currentEmail, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("request"); // "request" | "confirm" | "done"
  const [token, setToken] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [devToken, setDevToken] = useState(""); // shown in dev mode

  const requestChange = async () => {
    if (!newEmail.trim() || !password) {
      setError(t("edit_profile_extra.email_required"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post("/profile/email-change-request", {
        new_email: newEmail.trim(),
        current_password: password,
      });
      setSentTo(data.new_email);
      setDevToken(data.dev_token || "");
      setStep("confirm");
      setPassword(""); // clear password for security
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || "Fehler beim Anfordern"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmChange = async () => {
    if (!token.trim()) {
      setError(t("edit_profile_extra.token_required"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/profile/email-change-confirm", { token: token.trim() });
      setStep("done");
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || "Fehler beim Bestätigen"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
          <h3 className="text-sm font-bold text-[#0A0A0A] flex items-center gap-2">
            <Brand className="text-sm" variant="on-light" /> · {t("edit_profile_extra.change_email")}
          </h3>
          <button onClick={onClose} className="text-[#999] hover:text-[#0A0A0A]">✕</button>
        </div>
        <div className="px-4 py-3 space-y-3">
          {step === "request" && (
            <>
              <p className="text-xs text-[#666]">
                Aktuelle Adresse: <strong>{currentEmail}</strong>
              </p>
              <div>
                <label htmlFor="new-email" className="text-[10px] font-bold text-[#999] uppercase block mb-1">{t("edit_profile_extra.new_email")}</label>
                <input id="new-email" data-testid="new-email-input"
                  type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full h-10 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                  placeholder="neue@email.de" autoFocus />
              </div>
              <div>
                <label htmlFor="curr-pw" className="text-[10px] font-bold text-[#999] uppercase block mb-1">{t("edit_profile_extra.current_pw")}</label>
                <input id="curr-pw" data-testid="curr-pw-input"
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-10 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" />
                <p className="text-[10px] text-[#999] mt-1">{t("edit_profile_extra.verify_identity")}</p>
              </div>
              {error && (
                <InfoCard variant="danger">
                  <p className="text-[11px] text-red-700">{error}</p>
                </InfoCard>
              )}
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={onClose} className="flex-1 h-10 border border-[#E5E5E5] text-[#0A0A0A] text-sm">{t("common.cancel")}</button>
                <button type="button" data-testid="request-change-btn" onClick={requestChange} disabled={submitting || !newEmail || !password}
                  className="flex-1 rounded-full h-10 bg-[#0A0A0A] text-white text-sm font-medium disabled:opacity-50">
                  {submitting ? "Sende…" : "Bestätigungs-Mail senden"}
                </button>
              </div>
            </>
          )}

          {step === "confirm" && (
            <>
              <InfoCard variant="info">
                <p className="text-xs text-blue-800">
                  Eine Bestätigungs-Mail wurde an <strong>{sentTo}</strong> gesendet.
                  Bitte öffne den Bestätigungs-Link in der E-Mail oder gib den Token unten ein.
                </p>
              </InfoCard>
              {devToken && (
                <InfoCard variant="warning">
                  <p className="text-[10px] text-amber-800">
                    <strong>DEV-Modus:</strong> Token wird direkt angezeigt (in Production nur per Mail):
                  </p>
                  <code className="block mt-1 px-2 py-1 bg-white text-[10px] break-all font-mono">{devToken}</code>
                </InfoCard>
              )}
              <div>
                <label htmlFor="conf-token" className="text-[10px] font-bold text-[#999] uppercase block mb-1">{t("edit_profile_extra.confirm_token")}</label>
                <input id="conf-token" data-testid="confirm-token-input"
                  value={token} onChange={(e) => setToken(e.target.value)}
                  className="w-full h-10 px-3 border border-[#E5E5E5] text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                  placeholder={t("edit_profile_extra.ph_token")} />
              </div>
              {error && (
                <InfoCard variant="danger">
                  <p className="text-[11px] text-red-700">{error}</p>
                </InfoCard>
              )}
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setStep("request")} className="flex-1 h-10 border border-[#E5E5E5] text-[#0A0A0A] text-sm">
                  Zurück
                </button>
                <button type="button" data-testid="confirm-change-btn" onClick={confirmChange} disabled={submitting || !token}
                  className="flex-1 rounded-full h-10 bg-[#0A0A0A] text-white text-sm font-medium disabled:opacity-50">
                  {submitting ? "Bestätige…" : "E-Mail ändern"}
                </button>
              </div>
            </>
          )}

          {step === "done" && (
            <>
              <InfoCard variant="success" className="text-center">
                <p className="text-sm font-bold text-green-800">{t("edit_profile_extra.email_changed")}</p>
                <p className="text-xs text-green-700 mt-1">
                  Deine neue E-Mail-Adresse ist <strong>{sentTo}</strong>.
                </p>
              </InfoCard>
              <button type="button" data-testid="done-close-btn" onClick={() => { onSuccess(); }}
                className="w-full rounded-full h-10 bg-[#0A0A0A] text-white text-sm font-medium">
                Schließen
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
